import * as F from 'common/functions';
import classNames from 'classnames';
import { AlertStatus } from 'common/graphqlTypes';

import './TriggerHistoryGraph.scss';

type TriggerHistoryGraphOptions = {
  idleHeight?: number;
  idleColor?: string;
  idleYPosition?: number;
  triggeredHeight?: number;
  triggeredColor?: string;
  triggeredYPosition?: number;
  maxHistoryLength?: number;
  chartWidth?: number;
  chartHeight?: number;
  missingDataFiller?: AlertStatus;
};

const DEFAULT_GRAPH_OPTIONS = {
  idleHeight: 8,
  idleYPosition: 7,
  triggeredHeight: 15,
  triggeredYPosition: 0,
  maxHistoryLength: 60,
  chartWidth: 300,
  chartHeight: 18,
  missingDataFiller: AlertStatus.OK,
};

/**
 * Trigger history graph component.
 *
 * @param {array} triggerHistory  A list of 0s and 1s or booleans, representing idle and not triggered
 * @param {object} customOptions  The chart options.
 * @returns {JSX.Element} The trigger history graph
 */

type Props = {
  triggerHistory: Array<AlertStatus>;
  customOptions?: TriggerHistoryGraphOptions;
};

function TriggerHistoryGraph({ triggerHistory, customOptions = {} }: Props): JSX.Element {
  const graphOptions = {
    ...DEFAULT_GRAPH_OPTIONS,
    ...customOptions,
  };

  const triggerHistoryData = F.createSizedArray<AlertStatus>({
    sourceArray: triggerHistory,
    size: graphOptions.maxHistoryLength,
    filler: graphOptions.missingDataFiller,
    changeLeft: true,
  });

  const renderColumn = (alertStatus: AlertStatus, index: number): JSX.Element => {
    const columnClasses = classNames('TriggerHistoryGraph__Column', {
      'TriggerHistoryGraph__Column--Triggered': alertStatus === AlertStatus.TRIGGERED,
      'TriggerHistoryGraph__Column--Idle': alertStatus === AlertStatus.OK,
      'TriggerHistoryGraph__Column--Incomplete': alertStatus === AlertStatus.INCOMPLETE,
    });
    const key = `${alertStatus}-${index}}`;
    return <div key={key} className={columnClasses} role="presentation" />;
  };

  const renderColumns = triggerHistoryData.map(renderColumn);

  return (
    <div className="TriggerHistoryGraph" role="presentation">
      {renderColumns}
    </div>
  );
}

export default TriggerHistoryGraph;
